import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Offcanvas } from "react-bootstrap";
import "../Styles/agentChat.css";
import { FaBots } from "react-icons/fa6";
import { FiSend, FiX } from "react-icons/fi";

const AgentChat = () => {
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "bot",
      text: "Hi! 👋 I'm Galax AI Agent.",
      time: "Now",
    },
    {
      id: 2,
      type: "bot",
      text: "How can I help you today?",
      time: "Now",
    },
  ]);

  const messagesEndRef = useRef(null);

  // Close chat when token/storage changes
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "token" || event.key === null) {
        setShow(false);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [messages, isTyping]);

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSend = () => {
    const trimmedMessage = message.trim();

    if (!trimmedMessage || isTyping) return;

    const userMessage = {
      id: Date.now(),
      type: "user",
      text: trimmedMessage,
      time: getCurrentTime(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setIsTyping(true);

    // Demo AI response
    setTimeout(() => {
      setIsTyping(false);

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: "bot",
          text: getDemoResponse(trimmedMessage),
          time: getCurrentTime(),
        },
      ]);
    }, 1200);
  };

  const getDemoResponse = (text) => {
    const lowerText = text.toLowerCase();

    if (lowerText.includes("hello") || lowerText.includes("hi")) {
      return "Hello! 👋 What would you like me to help you with?";
    }

    if (lowerText.includes("help")) {
      return "Sure! I can help you with Galax EDC related questions, studies, subjects, forms and workflows.";
    }

    if (lowerText.includes("study")) {
      return "I can help you find information related to studies, subjects, visits and study workflows.";
    }

    if (lowerText.includes("form")) {
      return "I can help you with CRF forms, fields, visits and form-related information.";
    }

    return "Thanks for your message! 🤖 I'm still learning the Galax system. This response is currently running in demo mode.";
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating AI Button */}
      <Button
        type="button"
        className="galax-agent-launcher"
        onClick={() => setShow(true)}
        aria-label="Open Galax AI Agent"
        aria-expanded={show}
        aria-controls="galax-agent-chat"
      >
        <span className="agent-launcher-icon">
          <FaBots />
        </span>

        <span>AI Agent</span>

        <span className="agent-online-dot" />
      </Button>

      <Offcanvas
        id="galax-agent-chat"
        className="galax-agent-drawer"
        placement="end"
        show={show}
        onHide={() => setShow(false)}
      >
        {/* Header */}
        <Offcanvas.Header className="galax-agent-header">
          <div className="agent-header-content">
            <div className="agent-avatar">
              <FaBots />
              <span className="agent-status-dot" />
            </div>

            <div>
              <Offcanvas.Title className="agent-title">
                Galax AI Agent
              </Offcanvas.Title>

              <div className="agent-status">
                <span /> Online
              </div>
            </div>
          </div>

          <button
            type="button"
            className="agent-close-btn"
            onClick={() => setShow(false)}
            aria-label="Close"
          >
            <FiX />
          </button>
        </Offcanvas.Header>

        {/* Body */}
        <Offcanvas.Body className="galax-agent-body">
          <div className="galax-agent-messages">
            {/* Welcome */}
            <div className="agent-welcome">
              <div className="welcome-icon">
                <FaBots />
              </div>

              <h5>Welcome to Galax AI 👋</h5>

              <p>
                Ask me anything about your studies, subjects, forms,
                workflows or EDC data.
              </p>
            </div>

            {/* Messages */}
            <div className="agent-message-list">
              {messages.map((item) => (
                <div
                  key={item.id}
                  className={`agent-message-row ${
                    item.type === "user" ? "user-row" : "bot-row"
                  }`}
                >
                  {item.type === "bot" && (
                    <div className="small-agent-avatar">
                      <FaBots />
                    </div>
                  )}

                  <div
                    className={`agent-message ${
                      item.type === "user"
                        ? "agent-user-message"
                        : "agent-bot-message"
                    }`}
                  >
                    <div className="message-text">{item.text}</div>

                    <div className="message-time">{item.time}</div>
                  </div>
                </div>
              ))}

              {/* Typing */}
              {isTyping && (
                <div className="agent-message-row bot-row">
                  <div className="small-agent-avatar">
                    <FaBots />
                  </div>

                  <div className="agent-bot-message typing-message">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Composer */}
          <div className="galax-agent-composer">
            <div className="composer-box">
              <Form.Control
                id="galax-agent-message"
                as="textarea"
                rows={1}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Galax AI something..."
                disabled={isTyping}
              />

              <button
                type="button"
                className="agent-send-btn"
                onClick={handleSend}
                disabled={!message.trim() || isTyping}
                aria-label="Send message"
              >
                <FiSend />
              </button>
            </div>

            <div className="composer-hint">
              Press <strong>Enter</strong> to send •{" "}
              <strong>Shift + Enter</strong> for new line
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default AgentChat;
